import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate,Link } from 'react-router-dom';
import axios from 'axios';
import FeatherIcon from 'feather-icons-react';
import { API_URL, ENDPOINTS } from '../constants/api';
import { getToken, decodeToken } from '../utils/TokenUtils';
import { useTranslation } from 'react-i18next';
import { notification } from 'antd';
import ModalForm from './ModalForm';
import ReceiptForm from './ReceiptForm';

const Sidebar = ({ isMobileOpen, onCloseMobile }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = getToken();
  const userId = decodeToken(token, 'id');
  const companyName = decodeToken(token, 'companyName');
  const companyLogo = decodeToken(token, 'logo');
  const userType = decodeToken(token, 'userType');
  const dashboardPage = decodeToken(token, 'dashboardPage');
  const [categories, setCategories] = useState([]);
  const [links, setLinks] = useState([]);
  const [expandedCats, setExpandedCats] = useState({});
  const [expandedSubs, setExpandedSubs] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalProps, setModalProps] = useState({ userId: null, token: null, route: null });

  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    let linksUrl;
    const lowerName = companyName?.toLowerCase() || '';

    if (lowerName.includes('imaam') && lowerName.includes('shaafici')) {
      linksUrl = `${API_URL}/${ENDPOINTS.SYS}/get-categories2`;
    } else {
      linksUrl = `${API_URL}/${ENDPOINTS.SYS}/get-categories`;
    }

    const fetchLinks = async () => {
      try {
        const response = await axios.get(linksUrl, {
          headers: { Authorization: token ? `Bearer ${token}` : '' },
        });
        setCategories(response.data.categories || []);
        setLinks(response.data.links || []);
      } catch (error) {
        notification.error({
          message: `Error ${error?.response?.status || '500'}`,
          description: error?.response?.data?.message || 'Failed to fetch navigation items.',
        });
      }
    };

    fetchLinks();
  }, [token, companyName]);

  useEffect(() => {
    if (query.trim().length === 0) {
      setSearchResults([]);
      return;
    }

    const fetchSearchResults = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/${ENDPOINTS.SYS}/search-link?query=${encodeURIComponent(query)}`,
          { headers: { Authorization: token ? `Bearer ${token}` : '' } }
        );
        setSearchResults(response.data || []);
      } catch (error) {
        console.error('Search error:', error);
      }
    };

    const timer = setTimeout(fetchSearchResults, 300);
    return () => clearTimeout(timer);
  }, [query, token]);

  const toggleCategory = (catId) => {
    setExpandedCats((prev) => ({ ...prev, [catId]: !prev[catId] }));
  };

  const toggleSubCategory = (subKey) => {
    setExpandedSubs((prev) => ({
      ...prev,
      [subKey]: !prev[subKey]
    }));
  };

  const handleSearchClick = (e, path, route, tab) => {
    e.preventDefault();
    setQuery('');
    setSearchResults([]);

    if (onCloseMobile) onCloseMobile();

    if (path.includes('-modal/')) {
      setModalProps({ userId, token, route });
      setIsModalOpen(true);
    } else {
      if (tab === 'new-tab') {
        const fullPath = path.startsWith('http') ? path : `${window.location.origin}/${path}`;
        window.open(fullPath, '_blank', 'noopener,noreferrer');
      } else {
        navigate(path);
      }
    }
  };

  const closeModal = () => setIsModalOpen(false);

  return (
    <>
      <style>{`
        .ktc-sidebar {
          width: 270px;
          height: 100vh;
          position: fixed;
          top: 0;
          left: 0;
          background-color: #0f172a;
          color: #f8fafc;
          z-index: 1030;
          overflow-y: auto;
          border-right: 1px solid #1e293b;
          transition: transform 0.25s ease-in-out;
          font-size: 15px;
        }

        .sidebar-brand-header {
          height: 65px;
          border-bottom: 1px solid #1e293b;
          background: #0f172a;
        }

        .sidebar-search-input {
          font-size: 14px;
          height: 38px;
          background-color: #1e293b !important;
          border-color: #334155 !important;
          color: #ffffff !important;
        }

        .sidebar-search-input::placeholder {
          color: #94a3b8;
        }

        /* LEVEL 1: Main Categories */
        .category-btn {
          color: #f1f5f9;
          border: none;
          background: #1e293b;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 700;
          padding: 10px 8px;
          transition: all 0.15s ease;
        }

        .category-btn:hover {
          background: #334155;
          color: #ffffff;
        }

        /* LEVEL 2: Sub-categories */
        .subcategory-container {
          margin-left: 6px;
          padding-left: 5px;
          border-left: 2px solid #334155;
        }

        .subcategory-btn {
          color: #38bdf8;
          border: none;
          background: transparent;
          font-size: 14px;
          font-weight: 600;
          padding: 6px 10px;
          border-radius: 6px;
          transition: all 0.15s ease;
        }

        .subcategory-btn:hover {
          background: rgba(56, 189, 248, 0.1);
          color: #7dd3fc;
        }

        /* LEVEL 3: Final Navigation Links */
        .links-container {
          margin-left: 9px;
          padding-left: 8px;
          border-left: 1px dashed #475569;
        }

        .sidebar-link {
          display: flex;
          align-items: center;
          font-size: 14.4px;
          color: #94a3b8;
          text-decoration: none;
          padding: 5px 5px;
          border-radius: 6px;
          transition: all 0.15s ease;
          font-weight: 400;
        }

        .sidebar-link:hover {
          color: #ffffff;
          background: #1e293b;
        }

        .sidebar-link.active-link {
          background: #2563eb;
          color: #ffffff;
          font-weight: 600;
        }

        @media (max-width: 991.98px) {
          .ktc-sidebar {
            transform: translateX(-100%);
          }
          .ktc-sidebar.mobile-open {
            transform: translateX(0);
          }
        }
      `}</style>

      <aside className={`ktc-sidebar d-flex flex-column ${isMobileOpen ? 'mobile-open' : ''}`}>
        {/* Brand Header */}
        <div className="sidebar-brand-header px-3 d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2">
            <Link
                          className="navbar-brand d-flex align-items-center me-4"
                          to={`${window.location.origin}/${dashboardPage?.replace('/', '')}`}
                        >
            <img
              src={`${API_URL}/${companyLogo}`}
              alt="Logo"
              width="36"
              height="36"
              className="rounded-circle object-fit-cover border border-secondary"
              // onError={(e) => { e.target.src = 'https://via.placeholder.com/36'; }}
            />
            </Link>
             <span className="fw-bold text-slate-800 fs-6 tracking-tight">{companyName}</span>
             
          </div>
          {onCloseMobile && (
            <button className="btn btn-link text-white-50 d-lg-none p-0" onClick={onCloseMobile}>
              <FeatherIcon icon="x" size="22" />
            </button>
          )}
        </div>

        {/* Search Bar */}
        {userType === 'user' && (
          <div className="p-3 border-bottom border-secondary border-opacity-25 position-relative">
            <div className="input-group">
              <span className="input-group-text bg-transparent border-end-0 border-secondary border-opacity-50 ps-3" style={{ height: '38px' }}>
                <FeatherIcon icon="search" size="16" className="text-secondary" />
              </span>
              <input
                type="text"
                className="form-control sidebar-search-input border-start-0 rounded-end shadow-none"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`${t('Search menu')}...`}
              />
            </div>

            {searchResults.length > 0 && (
              <div className="bg-dark border border-secondary rounded p-2 mt-2">
                <ul className="list-unstyled mb-0">
                  {searchResults.map((item, index) => (
                    <li key={index} className="p-2 border-bottom border-secondary border-opacity-25">
                      <div className="text-white fw-medium" style={{ fontSize: '13.5px' }}>{t(item.name)}</div>
                      <div className="d-flex align-items-center gap-2 mt-2">
                        <button
                          className="btn btn-xs btn-primary rounded px-2 py-1"
                          style={{ fontSize: '12px' }}
                          onClick={(e) => handleSearchClick(e, item.full_route, item.route, 'current-tab')}
                        >
                          Open
                        </button>
                        <button
                          className="btn btn-xs btn-outline-light rounded px-2 py-1"
                          style={{ fontSize: '12px' }}
                          onClick={(e) => handleSearchClick(e, item.full_route, item.route, 'new-tab')}
                        >
                          New Tab
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Navigation List */}
        <div className="p-2 flex-grow-1">
          <ul className="nav flex-column gap-2">
            {categories.map((cat) => {
              const catLinks = links.filter((l) => l.category_id === cat.category_id);
              if (catLinks.length === 0) return null;

              const isCatExpanded = !!expandedCats[cat.category_id];

              const subCategoryGroup = catLinks.reduce((acc, item) => {
                const subName = item.sub_category || item.subcategory || null;
                if (subName) {
                  if (!acc[subName]) acc[subName] = [];
                  acc[subName].push(item);
                } else {
                  if (!acc['_direct']) acc['_direct'] = [];
                  acc['_direct'].push(item);
                }
                return acc;
              }, {});

              return (
                <li key={cat.category_id} className="nav-item">
                  {/* LEVEL 1: Category Header */}
                  <button
                    className="category-btn d-flex align-items-center w-100 justify-content-between"
                    onClick={() => toggleCategory(cat.category_id)}
                  >
                    <span className="d-flex align-items-center text-truncate">
                      <FeatherIcon icon={cat.category_icon || 'grid'} size="18" className="me-2 text-warning" />
                      <span className="text-truncate">{t(cat.category)}</span>
                    </span>
                    <FeatherIcon
                      icon="chevron-down"
                      size="16"
                      style={{
                        transition: 'transform 0.2s ease',
                        transform: isCatExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      }}
                    />
                  </button>

                  {/* LEVEL 2 Container */}
                  {isCatExpanded && (
                    <div className="subcategory-container my-1">
                      {Object.keys(subCategoryGroup).map((subKey) => {
                        // Direct Category Links
                        if (subKey === '_direct') {
                          return subCategoryGroup['_direct'].map((link) => (
                            <NavLink
                              key={link.id}
                              to={`/${link.full_route}`}
                              className={({ isActive }) =>
                                `sidebar-link ${isActive ? 'active-link' : ''}`
                              }
                              onClick={() => {
                                if (onCloseMobile) onCloseMobile();
                                if (link.full_route.includes('-modal')) {
                                  setModalProps({ userId, token, route: link.route });
                                  setIsModalOpen(true);
                                }
                              }}
                            >
                              <FeatherIcon icon="file-text" size="14" className="me-2 opacity-75" />
                              <span className="text-truncate">{t(link.name)}</span>
                            </NavLink>
                          ));
                        }

                        const subKeyId = `${cat.category_id}-${subKey}`;
                        const isSubExpanded = !!expandedSubs[subKeyId];
                        const count = subCategoryGroup[subKey].length;

                        return (
                          <div key={subKey} className="my-1">
                            {/* LEVEL 2: Subcategory Header */}
                            <button
                              className="subcategory-btn d-flex align-items-center justify-content-between w-100"
                              onClick={() => toggleSubCategory(subKeyId)}
                            >
                              <span className="d-flex align-items-center text-truncate me-1">
                                <FeatherIcon
                                  icon={isSubExpanded ? 'folder-minus' : 'folder'}
                                  size="15"
                                  className="me-2 text-info"
                                />
                                <span className="text-truncate">{t(subKey)}</span>
                              </span>
                              <div className="d-flex align-items-center gap-1">
                                <span className="badge bg-secondary bg-opacity-50 text-white-50 px-1" style={{ fontSize: '10px' }}>
                                  {count}
                                </span>
                                <FeatherIcon
                                  icon="chevron-right"
                                  size="14"
                                  style={{
                                    transition: 'transform 0.2s ease',
                                    transform: isSubExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                  }}
                                />
                              </div>
                            </button>

                            {/* LEVEL 3 Container: Final Links */}
                            {isSubExpanded && (
                              <div className="links-container my-1">
                                {subCategoryGroup[subKey].map((link) => (
                                  <NavLink
                                    key={link.id}
                                    to={`/${link.full_route}`}
                                    className={({ isActive }) =>
                                      `sidebar-link ${isActive ? 'active-link' : ''}`
                                    }
                                    onClick={() => {
                                      if (onCloseMobile) onCloseMobile();
                                      if (link.full_route.includes('-modal')) {
                                        setModalProps({ userId, token, route: link.route });
                                        setIsModalOpen(true);
                                      }
                                    }}
                                  >
                                    <FeatherIcon icon="disc" size="8" className="me-2 text-secondary" />
                                    <span className="text-truncate">{t(link.name)}</span>
                                  </NavLink>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </aside>

      {isModalOpen && modalProps?.route && (
        modalProps.route === 'new-receipt2' ? (
          <ReceiptForm
            userId={modalProps.userId}
            token={modalProps.token}
            route={modalProps.route}
            isOpen={isModalOpen}
            onRequestClose={closeModal}
          />
        ) : (
          <ModalForm
            userId={modalProps.userId}
            token={modalProps.token}
            route={modalProps.route}
            isOpen={isModalOpen}
            onRequestClose={closeModal}
          />
        )
      )}
    </>
  );
};

export default Sidebar;